import { gql, type QueryOptions } from "@apollo/client";

import {
  WorkflowConnectorFragmentDoc,
  WorkflowExtendedParameterFragmentDoc,
} from "@/modules/workflow/graphql/workflow.gql.ts";

import { stringifyLikeJson } from "../app-utils/graphql.js";
import { capitalizeFirstLetter } from "../app-utils/string.js";
import type {
  InputAdvancedSearchInput,
  InputAnalyzerIdMappingInput,
  InputAttributeValueInput,
  InputJobValidation,
  InputRegionWithoutStrand,
  InputSamplesValidationInputData,
  InputSequenceIdentifiersInput,
  InputSequenceUploadValidation,
  InputSetComparisonInput,
  InputTableQuerySpec,
  InputViewInput,
  Range,
  SavedItemsItemsArgs,
  SavedItemsListing,
  VariantAnalysisGroup,
} from "../graphql.js";
import type { ITableColumn } from "../redux/modules/table-data.js";
import {
  ATTRIBUTE_TYPE,
  CELL_TYPE,
  COLUMN_DYNAMIC_TYPES,
  ENTITY_TYPE,
  TABLE_CONTEXT_TYPE,
} from "../settings/settings.js";
import type { IProjectsListCardConfig } from "../ui-modules/dashboard/ProjectsDashboardCard.js";
import type { IRecentActivitiesCardConfig } from "../ui-modules/dashboard/RecentActivitiesDashboardCard.js";
import type { ISampleStatusesCardConfig } from "../ui-modules/dashboard/SampleStatusDashboardCard.js";
import type { ISavedItemsCardConfig } from "../ui-modules/dashboard/SavedItemsDashboardCard.js";
import { DynamicFragments, Fragments, GridFragments } from "./fragments.js";
import type { IDataTableQueryParams } from "./WebApiClient.js";

export interface IQueryOptions extends QueryOptions {
  rootField: string;
}

export function getGeneQueryOptions(geneId: number): IQueryOptions {
  return {
    query: gql`
      query getGene($geneId: Long!) {
        gene(geneId: $geneId) {
          ...GeneSummary
        }
      }
      ${Fragments.geneSummary}
    `,
    variables: {
      geneId: geneId,
    },
    rootField: "gene",
  };
}

export function getLoadGroupsOptions(type: string): IQueryOptions {
  return {
    query: gql`
      query loadSavedGroups($type: String!) {
        groups(type: $type) {
          name
          type
          itemCount
          items {
            id
            name
            itemCount
            project {
              id
              name
            }
          }
        }
      }
    `,
    variables: {
      type: type,
    },
    rootField: "groups",
  };
}

export function getLoadVariantAnalysisGroupOptions(
  type: string,
  variantGroupId: number,
  variantId: number,
): IQueryOptions {
  return {
    query: gql`
      query loadVariantAnalysisGroup($type: String, $mutationGroupId: Long, $entityId: Long) {
        variantAnalysisGroup(type: $type, mutationGroupId: $mutationGroupId, entityId: $entityId) {
          id
          name
        }
      }
    `,
    variables: {
      mutationGroupId: variantGroupId,
      entityId: variantId,
      type: type,
    },
    rootField: "variantAnalysisGroup",
  };
}

export function getLoadCompatibleVariantGroupsOptions(analysisGroupId: number): IQueryOptions {
  return {
    query: gql`
      query loadCompatibleVariantGroups($analysisGroupId: Long!) {
        savedVariantGroups(analysisGroupId: $analysisGroupId) {
          itemCount
          name
          type
          items {
            id
            name
            itemCount
            project {
              id
              name
            }
          }
        }
      }
    `,
    variables: {
      analysisGroupId: analysisGroupId,
    },
    rootField: "savedVariantGroups",
  };
}

export function getUserParamsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getUserParams {
        userParams {
          ...UserParams
        }
      }
      ${Fragments.userParams}
    `,
    variables: {},
    rootField: "userParams",
  };
}

export function getCurrentUserQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getCurrentUser {
        user {
          ...CurrentUser
        }
      }
      ${Fragments.currentUser}
    `,
    variables: {},
    rootField: "user",
  };
}

export function getCurrentUserCapabilitiesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getCurrentUser {
        user {
          capabilities {
            ...Capability
          }
        }
      }
      ${Fragments.capability}
    `,
    variables: {},
    rootField: "user",
  };
}

export function getUsersForAdminQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getUsersForAdmin {
        adminBrowser {
          allUsers {
            ...UserForAdmin
          }
        }
      }
      ${Fragments.userForAdmin}
    `,
    variables: {},
    rootField: "adminBrowser.allUsers",
  };
}

// do not get the user id, this fields needs the session and there is none at this state for potential users
export function getPossibleLdapUsersQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getPossibleLdapUsers {
        adminBrowser {
          allPossibleLdapUsers {
            username
            firstName
            lastName
            email
          }
        }
      }
    `,
    variables: {},
    rootField: "adminBrowser.allPossibleLdapUsers",
  };
}

export function getGenomeViewGenomeQueryOptions(genomeId: number, _contigLimit: number): IQueryOptions {
  return {
    query: gql`
      query getGenomeForGenomeBrowser($organismId: Long!) {
        organism(organismId: $organismId) {
          ...GenomeViewGenome
        }
      }
      ${Fragments.genomeForGenomeBrowser}
    `,
    variables: {
      organismId: genomeId,
    },
    rootField: "organism",
  };
}

export function getDefaultDatasetIdQueryOptions(organismId: number): IQueryOptions {
  return {
    query: gql`
      query getDefaultDatasetId($organismId: Long!) {
        defaultDataset(organismId: $organismId) {
          id
        }
      }
    `,
    variables: {
      organismId: organismId,
    },
    rootField: "defaultDataset.id",
  };
}

export function getDatasetQueryOptions(datasetId: number): IQueryOptions {
  return {
    query: gql`
      query getDataset($datasetId: Long!) {
        dataset(datasetId: $datasetId) {
          ...Dataset
        }
      }
      ${Fragments.dataset}
    `,
    variables: {
      datasetId: datasetId,
    },
    rootField: "dataset",
  };
}

export function getLightDatasetQueryOptions(datasetId: number): IQueryOptions {
  return {
    query: gql`
      query getDatasetLite($datasetId: Long!) {
        dataset(datasetId: $datasetId) {
          id
          name
          description
          columns {
            ...DatasetColumn
          }
        }
      }
      ${Fragments.datasetColumn}
    `,
    variables: {
      datasetId: datasetId,
    },
    rootField: "dataset",
  };
}

export function getDatasetMinMaxValuesQueryOptions(datasetId: number): IQueryOptions {
  return {
    query: gql`
      query getDatasetMinMaxValues($datasetId: Long!) {
        dataset(datasetId: $datasetId) {
          id
          columns {
            name
            field
            minValue
            maxValue
          }
        }
      }
    `,
    variables: {
      datasetId: datasetId,
    },
    rootField: "dataset",
  };
}

export function getGroupDatasetQueryOptions(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getGroupDataset($tableQuerySpec: InputTableQuerySpec!) {
        sample {
          dataset(tableQuerySpec: $tableQuerySpec) {
            ...Dataset
          }
        }
      }
      ${Fragments.dataset}
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "sample.dataset",
  };
}

export function getDataSeriesQueryOptions(datasetId: number, dataSeriesNames: string[]): IQueryOptions {
  return {
    query: gql`
      query getDataSeries($datasetId: Long!, $dataSeriesNames: [String]) {
        dataset(datasetId: $datasetId) {
          id
          dataSeries(dataSeriesNames: $dataSeriesNames) {
            ...DataSeries
          }
        }
      }
      ${Fragments.dataSeries}
    `,
    variables: {
      datasetId: datasetId,
      dataSeriesNames: dataSeriesNames,
    },
    rootField: "dataset.dataSeries",
  };
}

export function getCoordinateListsQueryOptions(coordinateListIds: number[]): IQueryOptions {
  return {
    query: gql`
      query getCoordinateLists($coordinateListIds: [Long!]) {
        coordinateLists(coordinateListIds: $coordinateListIds) {
          id
          name
          deleted
          hidden
        }
      }
    `,
    variables: {
      coordinateListIds: coordinateListIds,
    },
    rootField: "coordinateLists",
  };
}

export function getCoordinateListsByGenomeQueryOptions(genomeId: number): IQueryOptions {
  return {
    query: gql`
      query getCoordinateListsByGenome($genomeId: Long!) {
        coordinateListsByGenome(genomeId: $genomeId) {
          id
          name
        }
      }
    `,
    variables: {
      genomeId: genomeId,
    },
    rootField: "coordinateListsByGenome",
  };
}

export function getDataSeriesSearchQueryOptions(datasetId: number, searchTerm: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query searchDataSeries($datasetId: Long!, $searchTerm: String!, $limit: Int) {
        dataset(datasetId: $datasetId) {
          id
          dataSeriesSearch(dataSeriesSearchTerm: $searchTerm, limit: $limit) {
            name
          }
        }
      }
    `,
    variables: {
      datasetId: datasetId,
      searchTerm: searchTerm,
      limit: limit,
    },
    rootField: "dataset.dataSeriesSearch",
  };
}

export function getGroupDataSeriesQueryOptions(
  dataSeriesNames: string[],
  tableQuerySpec: InputTableQuerySpec,
): IQueryOptions {
  return {
    query: gql`
      query getGroupDataSeries($dataSeriesNames: [String], $tableQuerySpec: InputTableQuerySpec!) {
        sample {
          dataset(tableQuerySpec: $tableQuerySpec) {
            id
            dataSeries(dataSeriesNames: $dataSeriesNames) {
              ...DataSeries
            }
          }
        }
      }
      ${Fragments.dataSeries}
    `,
    variables: {
      dataSeriesNames: dataSeriesNames,
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "sample.dataset.dataSeries",
  };
}

export function getGroupDataSeriesSearchQueryOptions(
  searchTerm: string,
  limit: number,
  tableQuerySpec: InputTableQuerySpec,
): IQueryOptions {
  return {
    query: gql`
      query searchGroupDataSeries($searchTerm: String!, $limit: Int, $tableQuerySpec: InputTableQuerySpec!) {
        sample {
          dataset(tableQuerySpec: $tableQuerySpec) {
            id
            dataSeriesSearch(dataSeriesSearchTerm: $searchTerm, limit: $limit) {
              name
            }
          }
        }
      }
    `,
    variables: {
      searchTerm: searchTerm,
      limit: limit,
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "sample.dataset.dataSeriesSearch",
  };
}

export function getSequencedGenomeSearchQueryOptions(
  searchTerm: string,
  excludeVirtualGenomes: boolean,
  limit: number,
): IQueryOptions {
  return {
    query: gql`
      query searchSequencedGenome($searchTerm: String!, $limit: Int, $excludeVirtualGenomes: Boolean) {
        search {
          sequencedGenomes(searchTerm: $searchTerm, limit: $limit, excludeVirtualGenomes: $excludeVirtualGenomes) {
            genomes {
              ...OrganismForSearch
            }
          }
        }
      }
      ${Fragments.organismForSearch}
    `,
    variables: {
      searchTerm: searchTerm,
      limit: limit,
      excludeVirtualGenomes: excludeVirtualGenomes,
    },
    rootField: "search.sequencedGenomes",
  };
}

export function getSearchGenomesForSamplesQueryOptions(searchTerm: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query searchAssignableGenomes($searchTerm: String!, $limit: Int) {
        search {
          assignableGenomes(searchTerm: $searchTerm, limit: $limit) {
            genomes {
              ...OrganismForSearch
            }
          }
        }
      }
      ${Fragments.organismForSearch}
    `,
    variables: {
      searchTerm: searchTerm,
      limit: limit,
    },
    rootField: "search.assignableGenomes",
  };
}

export function getGenomeSearchQueryOptions(searchTerm: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query searchAllGenomes($searchTerm: String!, $limit: Int) {
        adminBrowser {
          allGenomes(searchTerm: $searchTerm, limit: $limit) {
            genomes {
              id
              name
              species {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      searchTerm: searchTerm,
      limit: limit,
    },
    rootField: "adminBrowser.allGenomes",
  };
}

export function getAvailableDatasetsQueryOptions(genomeId: number): IQueryOptions {
  return {
    query: gql`
      query getAvailableDatasets($organismId: Long!) {
        datasets(organismId: $organismId) {
          ...ListedDataset
        }
      }
      ${Fragments.listedDataset}
    `,
    variables: {
      organismId: genomeId,
    },
    rootField: "datasets",
  };
}

export function getGenomeSummaryQueryOptions(genomeId: number, _contigLimit: number): IQueryOptions {
  return {
    query: gql`
      query getGenomeSummary($organismId: Long!) {
        organism(organismId: $organismId) {
          ...GenomeSummary
        }
      }
      ${Fragments.genomeSummary}
    `,
    variables: {
      organismId: genomeId,
    },
    rootField: "organism",
  };
}

export function getGenomeGffFeatureQueryOptions(genomeId: number): IQueryOptions {
  return {
    query: gql`
      query getGenomeGffFeature($organismId: Long!) {
        organism(organismId: $organismId) {
          id
          genomeStatistics {
            geneWithoutPseudogeneCount
            pseudogeneCount
            mrnaCount
            exonCount
            cdsCount
            rrnaCount
            trnaCount
            snornaCount
            snrnaCount
            lncrnaCount
            ncrnaCount
            proteinsCount
          }
        }
      }
    `,
    variables: {
      organismId: genomeId,
    },
    rootField: "organism",
  };
}

export function getGenomeRelatedProjectsQueryOptions(genomeId: number): IQueryOptions {
  return {
    query: gql`
      query getGenomeRelatedProjects($organismId: Long!) {
        organism(organismId: $organismId) {
          ...GenomeRelatedProjects
        }
      }
      ${Fragments.genomeRelatedProjects}
    `,
    variables: {
      organismId: genomeId,
    },
    rootField: "organism",
  };
}

export function getGenomeAndSequenceStatisticsQueryOptions(genomeId: number): IQueryOptions {
  return {
    query: gql`
      query getGenomeAndSequenceStatistics($genomeId: Long) {
        genomeViewer {
          genomeAndSequenceStatistics(genomeId: $genomeId) {
            ...GenomeBrowserStatistics
          }
        }
      }
      ${Fragments.genomeBrowserStatistics}
    `,
    variables: {
      genomeId: genomeId,
    },
    rootField: "genomeViewer.genomeAndSequenceStatistics",
  };
}

export function getTrackFeaturesQueryOptions(
  featureType: string,
  contigId: number,
  range: Range,
  withSequence: boolean,
): IQueryOptions {
  return {
    query: gql`
      query getTrackFeatures($type: String!, $contigId: Long!, $rangeStart: Int!, $rangeStop: Int!) {
        genomeViewer {
          trackFeatures(type: $type, contigId: $contigId, rangeStart: $rangeStart, rangeStop: $rangeStop) {
            ...TrackFeatures
          }
        }
      }
      ${Fragments.trackFeatures(withSequence)}
    `,
    variables: {
      type: featureType,
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
    },
    rootField: "genomeViewer.trackFeatures",
  };
}

export function getTrackExpressionValuesQueryOptions(
  featureType: string,
  contigId: number,
  range: Range,
  datasetId: number,
): IQueryOptions {
  return {
    query: gql`
      query getTrackExpressionValues(
        $type: String!
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $datasetId: Long
      ) {
        genomeViewer {
          trackFeatures(
            type: $type
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            datasetId: $datasetId
          ) {
            ...TrackExpressionValues
          }
        }
      }
      ${Fragments.trackExpressionValues}
    `,
    variables: {
      type: featureType,
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      datasetId: datasetId,
    },
    rootField: "genomeViewer.trackFeatures",
  };
}

export function getFetchTrackCommunityAnnotationsQueryOptions(
  featureType: string,
  contigId: number,
  range: Range,
  featureSubtypeId?: number,
): IQueryOptions {
  return {
    query: gql`
      query getTrackCommunityAnnotations(
        $type: String!
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $subTypeId: Long
      ) {
        genomeViewer {
          trackGroupAnnotations(
            type: $type
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            subTypeId: $subTypeId
          ) {
            itemGroupIndicatorData {
              itemId
              groups {
                ...SavedItemBasics
              }
            }
            overflow
          }
        }
      }
      ${Fragments.savedItemBasics}
    `,
    variables: {
      type: featureType,
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      subTypeId: featureSubtypeId || undefined,
    },
    rootField: "genomeViewer.trackGroupAnnotations",
  };
}

export function getTrackNuAaFeaturesQueryOptions(
  featureType: string,
  featureTypeId: number,
  contigId: number,
  range: Range,
): IQueryOptions {
  return {
    query: gql`
      query getTrackNuAaFeatures(
        $type: String!
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $featureTypeId: Long
      ) {
        genomeViewer {
          trackFeatures(
            type: $type
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            featureTypeId: $featureTypeId
          ) {
            ...TrackFeatures
          }
        }
      }
      ${Fragments.trackFeatures(false)}
    `,
    variables: {
      type: featureType,
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      featureTypeId: featureTypeId,
    },
    rootField: "genomeViewer.trackFeatures",
  };
}

export function getTrackMarkerQueryOptions(markerTypeId: number, contigId: number, range: Range): IQueryOptions {
  return {
    query: gql`
      query getTrackMarkers(
        $type: String!
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $featureTypeId: Long
      ) {
        genomeViewer {
          trackFeatures(
            type: $type
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            featureTypeId: $featureTypeId
          ) {
            ...TrackFeatures
          }
        }
      }
      ${Fragments.trackFeatures(false)}
    `,
    variables: {
      type: ENTITY_TYPE.MARKER_NUS,
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      featureTypeId: markerTypeId,
    },
    rootField: "genomeViewer.trackFeatures",
  };
}

export function getProfileFeaturesQueryOptions(profileTypeId: number, contigId: number, range?: Range): IQueryOptions {
  return {
    query: gql`
      query getTrackNuProfiles($contigId: Long!, $rangeStart: Int!, $rangeStop: Int!, $profileTypeId: Long!) {
        genomeViewer {
          trackNuProfiles(
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            profileTypeId: $profileTypeId
          ) {
            ...ProfileFeatures
          }
        }
      }
      ${Fragments.profileFeatures}
    `,
    variables: {
      contigId: contigId,
      rangeStart: range?.start,
      rangeStop: range?.stop,
      profileTypeId: profileTypeId,
    },
    rootField: "genomeViewer.trackNuProfiles",
  };
}

export function getNgsCoverageFeaturesQueryOptions(
  bamFileId: number,
  contigId: number,
  range: Range,
  resolution: number,
): IQueryOptions {
  return {
    query: gql`
      query getNgsCoverageFeatures($bamFileId: Long!, $contigId: Long!, $range: InputRangeInput, $resolution: Int!) {
        genomeViewer {
          ngsCoverage(BAMId: $bamFileId, contigId: $contigId, range: $range, resolution: $resolution) {
            coverage
            range {
              start
              stop
            }
          }
        }
      }
    `,
    variables: {
      bamFileId: bamFileId,
      contigId: contigId,
      range: range ? { start: range.start, stop: range.stop } : undefined,
      resolution: resolution,
    },
    rootField: "genomeViewer.ngsCoverage",
  };
}

export function getNgsPileupFeaturesQueryOptions(
  bamFileId: number,
  contigId: number,
  range: Range,
  lanesCount: number,
  includeSoftClipped: boolean,
  maxElementCount = 10000,
): IQueryOptions {
  return {
    query: gql`
      query getNgsPileupFeatures(
        $lanesCount: Int!
        $bamFileId: Long!
        $region: InputRegionWithoutStrand
        $maxElementCount: Int!
        $includeSoftClipped: Boolean
      ) {
        genomeViewer {
          trackNgsReads(
            lanesCount: $lanesCount
            BAMId: $bamFileId
            region: $region
            maxElementCount: $maxElementCount
            includeSoftClipped: $includeSoftClipped
          ) {
            pileupElements {
              first {
                ...Data
              }
              second {
                ...Data
              }
            }
          }
        }
      }
      fragment Data on PileupAlignment {
        start
        end
        readLength
        readName
        mismatches
        strand
        flags
        mappingQuality
        templateStrand
        interChromosomal
        singleton
        split
        segments {
          start
          stop
          length
          mismatchesBases
          mismatchPositions
          mismatchBasesQualities
          # segment type, @see CIGAR
          match
          deletion
          insertion
          softClipped
        }
      }
    `,
    variables: {
      lanesCount,
      bamFileId,
      region: {
        contigId,
        range: { start: range.start, stop: range.stop },
      },
      maxElementCount,
      includeSoftClipped,
    },
    rootField: "genomeViewer.trackNgsReads",
  };
}

export function getBedFeaturesQueryOptions(bedFileId: number, contigId: number, range: Range): IQueryOptions {
  return {
    query: gql`
      query getBedFeatures($bedFileId: Long!, $contigId: Long!, $range: InputRangeInput) {
        genomeViewer {
          bedFeatures(bedFileId: $bedFileId, contigId: $contigId, range: $range) {
            ...TrackFeatures
          }
        }
      }
      ${Fragments.trackFeatures(false)}
    `,
    variables: {
      bedFileId,
      contigId,
      range,
    },
    rootField: "genomeViewer.bedFeatures",
  };
}

export function getCoordinateFeaturesQueryOptions(
  coordinateListId: number,
  contigId: number,
  range: Range,
): IQueryOptions {
  return {
    query: gql`
      query getCoordinateFeatures($coordinateListId: Long!, $contigId: Long!, $range: InputRangeInput) {
        genomeViewer {
          coordinateFeatures(coordinateListId: $coordinateListId, contigId: $contigId, range: $range) {
            ...TrackFeatures
          }
        }
      }
      ${Fragments.trackFeatures(false)}
    `,
    variables: {
      coordinateListId,
      contigId,
      range,
    },
    rootField: "genomeViewer.coordinateFeatures",
  };
}

export function getNgsReadTooltipQueryOptions(
  BAMId: number,
  pileupAlignmentStart: number,
  readName: string,
  region: InputRegionWithoutStrand,
): IQueryOptions {
  return {
    query: gql`
      query getReadTooltip(
        $BAMId: Long!
        $pileupAlignmentStart: Int!
        $readName: String!
        $region: InputRegionWithoutStrand!
      ) {
        genomeViewer {
          readTooltip(
            BAMId: $BAMId
            pileupAlignmentStart: $pileupAlignmentStart
            readName: $readName
            region: $region
          ) {
            attributes
            averageBaseQuality
            cigar
            deletions
            duplicate
            firstOfPair
            hasDeletions
            hasInsertions
            insertSize
            insertions
            mappingQuality
            mateMapped
            mateReferenceName
            mateStart
            mateStrand
            paired
            percentGC
            properPaired
            readGroup
            referenceName
            secondary
            strand
            templateStrand
          }
        }
      }
    `,
    variables: { BAMId, pileupAlignmentStart, readName, region },
    rootField: "genomeViewer.readTooltip",
  };
}

export function getBamFileSummaryQueryOptions(bamFileId: number): IQueryOptions {
  return {
    query: gql`
      query getBamFileSummary($bamFileId: Long!) {
        genomeViewer {
          bamSummary(bamFileId: $bamFileId) {
            id
            maxMappedReadLength
            minMappedReadLength
            bamFile {
              ...GenomeViewerFile
            }
          }
        }
      }
      ${Fragments.genomeViewerFile}
    `,
    variables: { bamFileId },
    rootField: "genomeViewer.bamSummary",
  };
}

export function getTrackVariantsQueryOptions(
  contigId: number,
  range: Range,
  refGenomeId: number,
  filterExpression = "",
  limit: number,
  mutantGenomeIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query getTrackVariants(
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $refGenomeId: Long
        $limit: Int
        $filterExpression: String
        $varGenomeIds: [Long]
      ) {
        genomeViewer {
          trackVariants(
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            refGenomeId: $refGenomeId
            limit: $limit
            filterExpression: $filterExpression
            varGenomeIds: $varGenomeIds
          ) {
            ...TrackVariants
          }
        }
      }
      ${Fragments.trackVariants}
    `,
    variables: {
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      refGenomeId: refGenomeId,
      limit: limit,
      filterExpression: filterExpression,
      varGenomeIds: mutantGenomeIds,
    },
    rootField: "genomeViewer.trackVariants",
  };
}

export function getTrackVcfVariantsQueryOptions(
  contigId: number,
  range: Range,
  refGenomeId: number,
  filterExpression = "",
  limit: number,
  mutantGenomeIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query getTrackVCFVariants(
        $contigId: Long!
        $rangeStart: Int!
        $rangeStop: Int!
        $refGenomeId: Long!
        $limit: Int
        $filterExpression: String
        $varGenomeIds: [Long]
      ) {
        genomeViewer {
          trackVCFVariants(
            contigId: $contigId
            rangeStart: $rangeStart
            rangeStop: $rangeStop
            refGenomeId: $refGenomeId
            limit: $limit
            filterExpression: $filterExpression
            varGenomeIds: $varGenomeIds
          ) {
            ...TrackVcfVariants
          }
        }
      }
      ${Fragments.trackVcfVariants}
    `,
    variables: {
      contigId: contigId,
      rangeStart: range.start,
      rangeStop: range.stop,
      refGenomeId: refGenomeId,
      limit: limit,
      filterExpression: filterExpression,
      varGenomeIds: mutantGenomeIds,
    },
    rootField: "genomeViewer.trackVCFVariants",
  };
}

export function getProjectQueryOptions(projectId: number): IQueryOptions {
  return {
    query: gql`
      query getProject($projectId: Long!) {
        project(projectId: $projectId) {
          ...Project
          parentFileId
        }
      }
      ${Fragments.project}
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "project",
  };
}

export function getProjectSequencedOrganismsQueryOptions(projectId: number): IQueryOptions {
  return {
    query: gql`
      query projectSequencedOrganisms($projectId: Long!) {
        project(projectId: $projectId) {
          ...ProjectSequencedOrganisms
        }
      }
      ${Fragments.projectSequenceOrganisms}
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "project.userGroup.sequencedOrganisms",
  };
}

export function getProjectActivityQueryOptions(activityId: number): IQueryOptions {
  return {
    query: gql`
      query getProjectActivity($activityId: Long!) {
        projectActivity(activityId: $activityId) {
          ...ProjectActivity
        }
      }
      ${Fragments.projectActivity}
    `,
    variables: {
      activityId: activityId,
    },
    rootField: "projectActivity",
  };
}

export function getProjectActivitiesQueryOptions(projectId: number): IQueryOptions {
  return {
    query: gql`
      query getProjectActivities($projectId: Long!) {
        projectActivities(projectId: $projectId) {
          ...ProjectActivity
        }
      }
      ${Fragments.projectActivity}
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "projectActivities",
  };
}

export function getProjectListQueryOptions(sampleTypeId: number, includeStagedProjects: boolean): IQueryOptions {
  return {
    query: gql`
      query getProjectsList($sampleTypeId: Long, $includeStagedProjects: Boolean) {
        projectList(sayId: $sampleTypeId, includeStagedProjects: $includeStagedProjects) {
          projects {
            ...ProjectBasics
            guid
            creator {
              display
            }
            ...ProjectMeta
          }
        }
      }
      ${Fragments.projectBasics}
      ${Fragments.projectMeta}
    `,
    variables: {
      sampleTypeId: sampleTypeId,
      includeStagedProjects: includeStagedProjects,
    },
    rootField: "projectList",
  };
}

export function getDashboardListQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getDashboardList {
        dashboards {
          id
          name
          sortOrder
          content
        }
      }
    `,
    variables: {},
    rootField: "dashboards",
  };
}

export function getDashboardCardProjectsListQueryOptions(config: IProjectsListCardConfig): IQueryOptions {
  return {
    query: gql`
      query getDashboardCardProjectsList($projectIds: [Long], $sortOrder: String) {
        projectList(projectIds: $projectIds, sortOrder: $sortOrder) {
          projects {
            ...ProjectBasics
          }
        }
      }
      ${Fragments.projectBasics}
    `,
    variables: {
      projectIds: config.includeProjects || [],
      sortOrder: config.sorting,
    },
    rootField: "projectList.projects",
  };
}

export function getDashboardCardRecentActivitiesListQueryOptions(_config: IRecentActivitiesCardConfig): IQueryOptions {
  return {
    query: gql`
      query getDashboardCardRecentActivititesList($limit: Int!) {
        recentItems(limit: $limit) {
          id
          name
          created
          description
          type
          subType
          path
          url
        }
      }
    `,
    variables: {
      limit: 10,
    },
    rootField: "recentItems",
  };
}

export function getProjectCapabilitiesListQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query get {
        allProjectCapabilities {
          id
          name
          description
        }
      }
    `,
    rootField: "allProjectCapabilities",
  };
}

export function getValidateProjectNameQueryOptions(projectName: string, ignoreId: number): IQueryOptions {
  return {
    query: gql`
      query validateProjectName($projectName: String!, $projectId: Long!) {
        projectNameDefined(projectName: $projectName, projectId: $projectId)
      }
    `,
    variables: {
      projectName: projectName,
      projectId: ignoreId,
    },
    rootField: "projectNameDefined",
  };
}

export function getValidateUsergroupNameQueryOptions(usergroupName: string, usergroupId: number): IQueryOptions {
  return {
    query: gql`
      query validateUserGroupName($usergroupName: String!, $usergroupId: Long) {
        userGroup {
          nameExists(userGroupInputData: { name: $usergroupName, userGroupId: $usergroupId })
        }
      }
    `,
    variables: {
      usergroupName: usergroupName,
      usergroupId: usergroupId,
    },
    rootField: "userGroup.nameExists",
  };
}

export function getValidateBlastInputSequenceQueryOptions(jobValidation: InputJobValidation): IQueryOptions {
  return {
    query: gql`
      query validateBlastInputSequence($jobValidationData: InputJobValidation) {
        blastInputValidation(jobValidationData: $jobValidationData) {
          message
          inputName
        }
      }
    `,
    variables: {
      jobValidationData: jobValidation,
    },
    rootField: "blastInputValidation",
  };
}

export function getValidateSequenceUploadQueryOptions(
  sequenceUploadValidationInput: Partial<InputSequenceUploadValidation>,
): IQueryOptions {
  return {
    query: gql`
      query validateSequencesUpload($validationData: InputSequenceUploadValidation) {
        sequenceUploadValidation(validationData: $validationData) {
          message
          inputName
        }
      }
    `,
    variables: {
      validationData: sequenceUploadValidationInput,
    },
    rootField: "sequenceUploadValidation",
  };
}

export function getSequenceIdentifiersQueryOptions(
  sequenceIdentifiersInput: InputSequenceIdentifiersInput,
): IQueryOptions {
  return {
    query: gql`
      query getSequenceIdentifiers($sequenceIdentifiersInputData: InputSequenceIdentifiersInput) {
        sequenceIdentifiers(sequenceIdentifiersInputData: $sequenceIdentifiersInputData)
      }
    `,
    variables: {
      sequenceIdentifiersInputData: sequenceIdentifiersInput,
    },
    rootField: "sequenceIdentifiers",
  };
}

export function getProteinQueryOptions(proteinId: number): IQueryOptions {
  return {
    query: gql`
      query getProtein($proteinId: Long!) {
        protein(proteinId: $proteinId) {
          ...ProteinSummary
        }
      }
      ${Fragments.proteinSummary}
    `,
    variables: {
      proteinId: proteinId,
    },
    rootField: "protein",
  };
}

export function getSavedGroupQueryOptions(groupId: number): IQueryOptions {
  return {
    query: gql`
      query getSavedGroup($groupId: Long!) {
        group(groupId: $groupId) {
          ...GroupBasics
          analysisGroup {
            ...VariantAnalysisGroup
          }
        }
      }
      ${Fragments.groupBasics}
      ${Fragments.variantAnalysisGroup}
    `,
    variables: {
      groupId: groupId,
    },
    rootField: "group",
  };
}

export function getSavedViewQueryOptions(viewId: number): IQueryOptions {
  return {
    query: gql`
      query getSavedView($viewId: Long!) {
        view(viewId: $viewId) {
          ...View
        }
      }
      ${Fragments.view}
    `,
    variables: {
      viewId: viewId,
    },
    rootField: "view",
  };
}

export function getSavedItemsListingQueryOptions(projectId: number): IQueryOptions {
  return {
    query: gql`
      query getSavedItemsMeta($projectId: Long!) {
        savedItems(projectId: $projectId) {
          listing {
            ...SavedItemsListing
          }
        }
      }
      ${Fragments.savedItemListing}
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "savedItems.listing",
  };
}

export function getSavedItemsQueryOptions(projectId: number, type: string, subType: string): IQueryOptions {
  return {
    query: gql`
      query getSavedItems($projectId: Long!, $type: String!, $subType: String!) {
        savedItems(projectId: $projectId) {
          items(type: $type, subType: $subType) {
            id
            ...VariadicSavedItem
          }
        }
      }
      ${Fragments.variadicSavedItem}
    `,
    variables: {
      projectId: projectId,
      type: type,
      subType: subType,
    },
    rootField: "savedItems.items",
  };
}

export function getDashboardCardSavedItemsQueryOptions(
  config: ISavedItemsCardConfig,
  activeSavedItemsListing: SavedItemsItemsArgs,
  filterTerm: string,
): IQueryOptions {
  return {
    query: gql`
      query getSavedItems($projectId: Long!, $type: String!, $subType: String!, $limit: Int, $filterTerm: String) {
        savedItems(projectId: $projectId, limit: $limit, filterTerm: $filterTerm) {
          items(type: $type, subType: $subType) {
            ...VariadicCardSavedItem
          }
        }
      }
      ${Fragments.variadicCardSavedItem}
    `,
    variables: {
      projectId: -1,
      type: activeSavedItemsListing.type,
      subType: activeSavedItemsListing.subType,
      limit: config.limit,
      filterTerm: filterTerm,
    },
    rootField: "savedItems.items",
  };
}

export function getSavedItemsForAttachmentsQueryOptions(
  projectId: number,
  listing: SavedItemsListing[],
): IQueryOptions {
  const aliases = listing.map((listingData: SavedItemsListing, index: number) => {
    return ` ${listingData.type}${index}: items(type: "${listingData.type}", subType: "${listingData.subType}") { ...SavedItemForAttachment } `;
  });
  return {
    query: gql`
      query getSavedItemsForAttachments ($projectId: Long!) {
        savedItems(projectId: $projectId) {
          ${aliases}
        }
      }
      ${Fragments.savedItemForAttachment}
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "savedItems",
  };
}

export function getSearchResultQueryOptions(searchResultId: number): IQueryOptions {
  return {
    query: gql`
      query getSearchResult($searchResultId: Long!) {
        search {
          searchResult(searchResultId: $searchResultId) {
            ...SearchResult
          }
        }
      }
      ${Fragments.searchResult}
    `,
    variables: {
      searchResultId: searchResultId,
    },
    rootField: "search.searchResult",
  };
}

export function getTranscriptQueryOptions(transcriptId: number): IQueryOptions {
  return {
    query: gql`
      query getTranscript($transcriptId: Long!) {
        transcript(transcriptId: $transcriptId) {
          ...TranscriptSummary
        }
      }
      ${Fragments.transcriptSummary}
    `,
    variables: {
      transcriptId: transcriptId,
    },
    rootField: "transcript",
  };
}

export function getContigSummaryQueryOptions(contigId: number): IQueryOptions {
  return {
    query: gql`
      query getContigSummary($contigId: Long!) {
        contig(contigId: $contigId) {
          ...ContigSummary
        }
      }
      ${Fragments.contigSummary}
    `,
    variables: {
      contigId: contigId,
    },
    rootField: "contig",
  };
}

export function getSampleQueryOptions(sampleId: number): IQueryOptions {
  return {
    query: gql`
      query getSample($sampleId: Long!) {
        sample {
          sample(sampleId: $sampleId) {
            ...SampleSummary
          }
        }
      }
      ${Fragments.sampleSummary}
    `,
    variables: {
      sampleId: sampleId,
    },
    rootField: "sample.sample",
  };
}

export function getSampleValuesQueryOptions(sampleId: number): IQueryOptions {
  return {
    query: gql`
      query getSampleValues($sampleId: Long!) {
        sample {
          sample(sampleId: $sampleId) {
            ...SampleValues
          }
        }
      }
      ${Fragments.sampleValues}
    `,
    variables: {
      sampleId: sampleId,
    },
    rootField: "sample.sample.allApplicableValues",
  };
}

export function getSampleParentQueryOptions(parentId: number): IQueryOptions {
  return {
    query: gql`
      query getParent($parentId: Long!) {
        sample {
          parent(parentId: $parentId) {
            ...SampleParentSummary
          }
        }
      }
      ${Fragments.sampleParentSummary}
    `,
    variables: {
      parentId: parentId,
    },
    rootField: "sample.parent",
  };
}

export function getSampleParentStatisticsQueryOptions(parentIds: number[]): IQueryOptions {
  return {
    query: gql`
      query getParentStatistics($parentIds: [Long]) {
        sample {
          parentStatistics(parentIds: $parentIds) {
            reportCount
            sampleCount
          }
        }
      }
    `,
    variables: {
      parentIds: parentIds,
    },
    rootField: "sample.parentStatistics",
  };
}

export function getSampleTypesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getSampleTypes {
        sample {
          sampleTypes {
            id
            name
            sampleStatuses {
              id
              name
              sortOrder
              statusClass {
                id
                name
              }
            }
          }
        }
      }
    `,
    rootField: "sample.sampleTypes",
  };
}

export function getAdminSampleTypesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getAdminSampleTypes {
        sample {
          sampleTypesAdmin {
            ...SampleType
            playbooks {
              id
              name
            }
          }
        }
      }
      ${Fragments.sampleType}
    `,
    rootField: "sample.sampleTypesAdmin",
  };
}

export function getAdminSampleStatusSetsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getAdminSampleStatusSets {
        sample {
          sampleStatusSets {
            id
            name
            description
          }
        }
      }
    `,
    rootField: "sample.sampleStatusSets",
  };
}

export function getAdminParentTypesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getParentTypes {
        sample {
          parentTypes {
            ...ParentType
          }
        }
      }
      ${Fragments.parentType}
    `,
    rootField: "sample.parentTypes",
  };
}

export function getMinimalParentTypesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getMinimalParentTypes {
        sample {
          parentTypes {
            ...MinimalParentType
          }
        }
      }
      ${Fragments.MinimalParentType}
    `,
    rootField: "sample.parentTypes",
  };
}

export function getParentTypesForSampleTypeCreationQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getParentTypesForSampleTypeCreation {
        sample {
          parentTypes {
            ...ParentTypeForSampleTypeEditing
          }
        }
      }
      ${Fragments.parentTypeForSampleTypeEditing}
    `,
    rootField: "sample.parentTypes",
  };
}

export function getAdminParentTypeQueryOptions(parentTypeId: number): IQueryOptions {
  return {
    query: gql`
      query getParentType($parentTypeId: Long!) {
        sample {
          parentType(parentTypeId: $parentTypeId) {
            ...ParentTypeForEditing
          }
        }
      }
      ${Fragments.parentTypeForEditing}
    `,
    variables: {
      parentTypeId: parentTypeId,
    },
    rootField: "sample.parentType",
  };
}

export function getAdminSampleTypeQueryOptions(sampleTypeId: number): IQueryOptions {
  return {
    query: gql`
      query getAdminSampleType($sampleTypeId: Long!) {
        sample {
          sampleTypeAdmin(sampleTypeId: $sampleTypeId) {
            ...SampleTypeForEditing
          }
        }
      }
      ${Fragments.sampleTypeForEditing}
    `,
    variables: {
      sampleTypeId: sampleTypeId,
    },
    rootField: "sample.sampleTypeAdmin",
  };
}

export function getSampleStatusesQueryOptions(statusSetId: number): IQueryOptions {
  return {
    query: gql`
      query getSampleStatuses($statusSetId: Long!) {
        sample {
          sampleStatuses(statusSetId: $statusSetId) {
            id
            name
            statusClass {
              id
              name
            }
          }
        }
      }
    `,
    variables: {
      statusSetId: statusSetId,
    },
    rootField: "sample.sampleStatuses",
  };
}

export function getSampleAttributeDefaultsQueryOptions(sampleTypeId: number): IQueryOptions {
  return {
    query: gql`
      query getSampleAttributeDefaults($sampleTypeId: Long!) {
        sample {
          attributeDefaults(sampleTypeId: $sampleTypeId) {
            attributeId
            data {
              defaultNumber
              defaultText
              template
              defaultTerm {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      sampleTypeId: sampleTypeId,
    },
    rootField: "sample.attributeDefaults",
  };
}

export function getRawSampleAttributeDefaultsQueryOptions(sampleTypeId: number): IQueryOptions {
  return {
    query: gql`
      query getRawSampleAttributeDefaults($sampleTypeId: Long!) {
        sample {
          rawAttributeDefaults(sampleTypeId: $sampleTypeId) {
            ...RawAttributeDefault
          }
        }
      }
      ${Fragments.rawAttributeDefault}
    `,
    variables: {
      sampleTypeId: sampleTypeId,
    },
    rootField: "sample.rawAttributeDefaults",
  };
}

export function getAttributesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getSampleAttributes {
        sample {
          attributes {
            ...SampleAttribute
            sampleTypes {
              id
              name
            }
          }
        }
      }
      ${Fragments.sampleAttribute}
    `,
    variables: {},
    rootField: "sample.attributes",
  };
}

export function getAttributesAsDecorationQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getSampleAttributesAsDecoration {
        sample {
          attributes {
            id
            attributeName
            attributeType {
              id
              name
            }
            groupName
            multipleValues
            representedColumn
            columnSet {
              id
              name
            }
          }
        }
      }
    `,
    variables: {},
    rootField: "sample.attributes",
  };
}

export function getAttributeQueryOptions(attributeId: number): IQueryOptions {
  return {
    query: gql`
      query getSampleAttribute($attributeId: Long!) {
        sample {
          attribute(attributeId: $attributeId) {
            ...SampleAttribute
            referencedBy {
              id
            }
            sampleTypes {
              id
            }
          }
        }
      }
      ${Fragments.sampleAttribute}
    `,
    variables: {
      attributeId: attributeId,
    },
    rootField: "sample.attribute",
  };
}

export function getSequenceQueryOptions(sequenceId: number): IQueryOptions {
  return {
    query: gql`
      query getSequence($sequenceId: Long!) {
        sequence(sequenceId: $sequenceId) {
          ...SequenceSummary
        }
      }
      ${Fragments.sequenceSummary}
    `,
    variables: {
      sequenceId: sequenceId,
    },
    rootField: "sequence",
  };
}

export function getGenMutationQueryOptions(mutationId: number, refGenomeId: number): IQueryOptions {
  return {
    query: gql`
      query getGenMutation($mutationId: Long!, $refGenomeId: Long!) {
        mutation(mutationId: $mutationId, refGenomeId: $refGenomeId) {
          ...GenMutation
        }
      }
      ${Fragments.genMutation}
    `,
    variables: {
      mutationId: mutationId,
      refGenomeId: refGenomeId,
    },
    rootField: "mutation",
  };
}

// export function getToolsForAdminQueryOptions(): IQueryOptions {
//   return {
//     query: gql`
//       query getToolsForAdmin {
//         adminBrowser {
//           allTools {
//             id
//             name
//             description
//             url
//           }
//         }
//       }
//     `,
//     variables: {},
//     rootField: "adminBrowser.allTools"
//   };
// }

export function createDynamicFragments(
  dynamicColumns: ITableColumn[],
  separator?: string,
): { dynamicFields: string; dynamicTypes: string[]; additionalFragments: string[] } {
  const mySep: string = separator || ".";
  const dynamicFields: string[] = [];
  const dynamicTypes: string[] = [];
  const additionalFragments: string[] = [];

  // the dynamic path might be 'classification36.displayName' or 'protein.classification36.displayName'
  // therefore we have to split the path into 'path.id.fieldValueGetter'
  dynamicColumns.forEach((col, _index) => {
    const data = col.field.split(mySep);

    let fieldValueGetterParts = 1;
    if (col.cellType === CELL_TYPE.FILE || col.attribute?.attributeType?.id === ATTRIBUTE_TYPE.DYNAMIC_TERMS) {
      fieldValueGetterParts = 2; // because the dynamic path will be e.g. protein.attribute222.projectFile.fileName therefore we need to adapt the field parsing
    }

    const fieldId = data[data.length - fieldValueGetterParts - 1]; // e.g. attribute222
    let fieldValueGetter = data[data.length - fieldValueGetterParts]; // e.g. stringValue or projectFile
    if (col.cellType === CELL_TYPE.FILE) {
      fieldValueGetter += " { ...ProjectFileTile }"; // should correspond to FileTile
      additionalFragments.push("projectFileTile");
    }

    if (col.attribute?.attributeType?.id === ATTRIBUTE_TYPE.DYNAMIC_TERMS) {
      fieldValueGetter += " {...DynamicTerm}";
      additionalFragments.push("dynamicTerm");
    }

    let alias = `
      ${fieldId}: ${col.dynamicType}(${col.dynamicType}: "${fieldId}") {
        ${fieldValueGetter}
        ${DynamicFragments[col.dynamicType]}
      }
    `;
    if (data.length > fieldValueGetterParts + 1) {
      // some more dynamic nesting might be needed, depending on the nesting !
      for (let fieldPathIndex = data.length - fieldValueGetterParts - 2; fieldPathIndex >= 0; fieldPathIndex--) {
        alias = ` ${data[fieldPathIndex]} { ${alias} }`;
      }
    }
    dynamicFields.push(alias);
    dynamicTypes.push(col.dynamicType);
  });

  return {
    dynamicFields: dynamicFields.join("\n"),
    dynamicTypes: dynamicTypes,
    additionalFragments: additionalFragments,
  };
}

function getGraphQLEntityName(tableQuerySpec: InputTableQuerySpec) {
  switch (tableQuerySpec.resultType) {
    case ENTITY_TYPE.MUTATION:
      return "GenMutation";
    case ENTITY_TYPE.VCF_VARIANT:
      return "VCFVariant";
    case ENTITY_TYPE.ANALYST_SESSION:
      return "AnalystSession";
    case ENTITY_TYPE.WORKFLOW_SESSION:
      return "WorkflowSession";
    case ENTITY_TYPE.BED_FEATURE:
      return "BEDFeature";
    case ENTITY_TYPE.GENERIC_SEQ:
      return "Sequence";
    default:
      return capitalizeFirstLetter(tableQuerySpec.resultType);
  }
}

export function getDataTableQueryOptions(queryParams: IDataTableQueryParams, columns?: ITableColumn[]): IQueryOptions {
  const tableQuerySpec: InputTableQuerySpec = queryParams.tableQuerySpec;

  const gridFragmentKey: string = tableQuerySpec.resultType + "Grid";
  const gridFragmentName: string = capitalizeFirstLetter(gridFragmentKey);

  const isAdminContext: boolean = queryParams.tableQuerySpec.context === TABLE_CONTEXT_TYPE.ADMIN;
  const rootQueryContainer: string = isAdminContext ? "admin" : null;
  const rootFieldValue = rootQueryContainer ? `${rootQueryContainer}.table` : `table`;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { context, ...cleanedUpTableQuerySpec } = queryParams.tableQuerySpec; // remove the context
  const queryName = `get${capitalizeFirstLetter(tableQuerySpec.resultType)}Data${
    queryParams.withPageRows ? "Rows" : "Table"
  }`;

  const dynamicColumns: ITableColumn[] = columns ? columns.filter((col) => col.dynamic && !col.hidden) : [];
  const { dynamicFields, dynamicTypes, additionalFragments } = createDynamicFragments(dynamicColumns);

  return {
    query: gql`
      query ${queryName} ($tableQuerySpec: InputTableQuerySpec) {
        ${rootQueryContainer ? rootQueryContainer + " {" : ""}
        table(tableQuerySpec: $tableQuerySpec) {
          rowIds @include(if: ${!!queryParams.withRowIds})
          totalRowCount  @include(if: ${!!queryParams.withPagination})
          filteredRowCount  @include(if: ${!!queryParams.withPagination})
          restrictedRowCount  @include(if: ${!!queryParams.withPagination})
          pageRows @include(if: ${!!queryParams.withPageRows})  {
            id
            groupType
            ...${gridFragmentName}
            ... on ${getGraphQLEntityName(tableQuerySpec)} {
              id
              ${dynamicFields}
            }
          }
          columns @include(if: ${!!queryParams.withColumns}) {
             ...GridColumn
          }
          templates @include(if: ${!!queryParams.withColumns}) {
            ...Template
          }
        }
        ${rootQueryContainer ? "}" : ""}
      }
      ${Fragments.gridColumn}
      ${Fragments.columnTemplate}
      ${GridFragments[gridFragmentKey](queryParams)}
      ${dynamicTypes.includes(COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE) ? Fragments.gridAttributeValue : ""}
      ${dynamicTypes.includes(COLUMN_DYNAMIC_TYPES.CUSTOM_FILE_COLUMN) ? Fragments.gridFileCustomColumn : ""}
      ${dynamicTypes.includes(COLUMN_DYNAMIC_TYPES.CLASSIFICATION) ? Fragments.classificationBasics : ""}
      ${dynamicTypes.includes(COLUMN_DYNAMIC_TYPES.EXTERNAL_REFERENCE) ? Fragments.externalReference : ""}
      ${dynamicTypes.includes(COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY) ? Fragments.gridOrganismProperty : ""}
      ${additionalFragments.includes("projectFileTile") ? Fragments.projectFileTile : ""}
      ${additionalFragments.includes("dynamicTerm") ? Fragments.dynamicTerm : ""}
    `,
    // would be much nicer to be able to spread directly the fragments from a list (instead of the individual "includes" above)! but the tag literal option of gql does not allow that, not sure how to.
    // the alternative option was to use gql(``, ...args) however this does not work above graphql-tag@2.11.0 therefore we had to move into this unelegant fragment injection mechanism
    variables: {
      tableQuerySpec: cleanedUpTableQuerySpec,
    },
    rootField: rootFieldValue,
  };
}

export function getVariantRelatedGenomes(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getVariantRelatedGenomes($tableQuerySpec: InputTableQuerySpec) {
        variantRelatedGenomes(tableQuerySpec: $tableQuerySpec)
      }
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "variantRelatedGenomes",
  };
}

export function getColumnQueryOptions(columnId: number, tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getTableColumn($tableQuerySpec: InputTableQuerySpec, $columnId: Long) {
        table(tableQuerySpec: $tableQuerySpec) {
          column(columnId: $columnId) {
            ...GridColumn
          }
        }
      }
      ${Fragments.gridColumn}
    `,
    variables: {
      columnId: columnId,
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "table.column",
  };
}

export function getNotificationQueryOptions(notificationId: number): IQueryOptions {
  return {
    query: gql`
      query getNotification($notificationId: Long!) {
        notification(notificationId: $notificationId) {
          ...Notification
        }
      }
      ${Fragments.notification}
    `,
    variables: {
      notificationId: notificationId,
    },
    rootField: "notification",
  };
}

export function getNotificationListQueryOptions(readNotificationsLimit: number): IQueryOptions {
  return {
    query: gql`
      query getNotificationsList($readNotificationsLimit: Int!) {
        notifications(oldNotificationLimit: $readNotificationsLimit) {
          ...Notification
        }
      }
      ${Fragments.notification}
    `,
    variables: {
      readNotificationsLimit: readNotificationsLimit,
    },
    rootField: "notifications",
  };
}

export function getContigForGenomeBrowserQueryOptions(contigId: number): IQueryOptions {
  return {
    query: gql`
      query getContigForGenomeBrowser($contigId: Long!) {
        contig(contigId: $contigId) {
          ...GenomeViewerContig
        }
      }
      ${Fragments.genomeViewerContig}
    `,
    variables: {
      contigId: contigId,
    },
    rootField: "contig",
  };
}

export function getContigSearchResultsQueryOptions(genomeId: number, searchTerm: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query searchContig($searchTerm: String, $genomeId: Long, $limit: Int) {
        search {
          contigSearch(searchTerm: $searchTerm, genomeId: $genomeId, limit: $limit) {
            ...ContigSearch
          }
        }
      }
      ${Fragments.contigSearch}
    `,
    variables: {
      genomeId: genomeId,
      searchTerm: searchTerm,
      limit: limit,
    },
    rootField: "search.contigSearch",
  };
}

export function getTableDataRowPositionQueryOptions(
  targetId: number,
  _targetType: string,
  tableQuerySpec: InputTableQuerySpec,
): IQueryOptions {
  return {
    query: gql`
      query getTableDataRowPosition($tableQuerySpec: InputTableQuerySpec, $itemId: Long) {
        table(tableQuerySpec: $tableQuerySpec) {
          rowIndex(itemId: $itemId) {
            id
            rowIndex
          }
        }
      }
    `,
    variables: {
      itemId: targetId,
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "table.rowIndex",
  };
}

export function getBlastIndexListQueryOptions(searchTerm: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query getBlastIndicesList($searchTerm: String!, $limit: Int) {
        blastIndices(searchTerm: $searchTerm, limit: $limit) {
          ...BlastIndex
        }
      }
      ${Fragments.blastIndex}
    `,
    variables: {
      searchTerm: searchTerm,
      limit: limit,
    },
    rootField: "blastIndices",
  };
}

export function getJobQueryOptions(jobId: number, bestHit = true): IQueryOptions {
  return {
    query: gql`
      query getJob($jobId: Long!, $bestHit: Boolean) {
        job(jobId: $jobId) {
          id
          ... on BlastJob {
            ...Job
          }
        }
      }
      ${Fragments.job}
    `,
    variables: {
      jobId: jobId,
      bestHit: bestHit,
    },
    rootField: "job",
  };
}

export function getJobForRerunQueryOptions(jobId: number): IQueryOptions {
  return {
    query: gql`
      query getJob($jobId: Long!) {
        job(jobId: $jobId) {
          id
          ... on BlastJob {
            ...JobForRerun
          }
        }
      }
      ${Fragments.jobForRerun}
    `,
    variables: {
      jobId: jobId,
    },
    rootField: "job",
  };
}

export function getJobResultsQueryOptions(jobId: number, queryIds: number[], bestHit = false): IQueryOptions {
  return {
    query: gql`
      query getJobResults($jobId: Long!, $queryIds: [Long], $bestHit: Boolean) {
        job(jobId: $jobId) {
          id
          ... on BlastJob {
            results(queryIds: $queryIds) {
              ...JobResult
            }
          }
        }
      }
      ${Fragments.jobResult}
    `,
    variables: {
      jobId: jobId,
      queryIds: queryIds,
      bestHit: bestHit,
    },
    rootField: "job.results",
  };
}

export function getContentBrowserCountsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getContentBrowserCounts {
        contentBrowser {
          organisms {
            count
          }
        }
      }
    `,
    variables: {},
    rootField: "contentBrowser",
  };
}

export function getUserGroupAdminCountsQueryOptions(projectGroupAdminKeys: string[]): IQueryOptions {
  return {
    query: gql`
      query getUserGroupAdminCounts {
        adminBrowser {
          ${projectGroupAdminKeys.map((key: string) => `${key} { count }`)}
        }
      }
    `,
    variables: {},
    rootField: "adminBrowser",
  };
}

export function getFetchUserGroupsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getUserGroups {
        userGroup {
          userGroups {
            ...UserGroup
          }
        }
      }
      ${Fragments.userGroup}
    `,
    variables: {},
    rootField: "userGroup.userGroups",
  };
}

export function getFetchBasicUserGroups(): IQueryOptions {
  return {
    query: gql`
      query getBasicUserGroups {
        userGroup {
          userGroups {
            ...UserGroupBasics
          }
        }
      }
      ${Fragments.userGroupBasics}
    `,
    variables: {},
    rootField: "userGroup.userGroups",
  };
}

export function getAccessibleUserGroups(): IQueryOptions {
  return {
    query: gql`
      query getAccessibleUserGroups {
        userGroup {
          accessibleUserGroups {
            ...UserGroupBasics
          }
        }
      }
      ${Fragments.userGroupBasics}
    `,
    variables: {},
    rootField: "userGroup.accessibleUserGroups",
  };
}

export function getContentBrowserListingQueryOptions(featureType: string, groupByAttribute: string): IQueryOptions {
  return {
    query: gql`
      query getContentBrowserListing {
        contentBrowser {
          ${featureType} {
        ${groupByAttribute} {
        listing {
          ...ListingMapEntry
        }
      }
      }
      }
      }
      ${Fragments.listingMapEntry}
    `,
    variables: {},
    rootField: "contentBrowser",
  };
}

export function getAdminBrowserListingQueryOptions(featureType: string, groupByAttribute: string): IQueryOptions {
  return {
    query: gql`
      query getAdminBrowserListing {
        adminBrowser {
          ${featureType} {
        ${groupByAttribute} {
        listing {
          ...ListingMapEntry
        }
      }
      }
      }
      }
      ${Fragments.listingMapEntry}
    `,
    variables: {},
    rootField: "adminBrowser",
  };
}

export function getContentBrowserInfoQueryOptions(
  featureType: string,
  groupByAttribute: string,
  filterKey: string,
): IQueryOptions {
  return {
    query: gql`
      query getContentBrowserInfo ($filterKey: String) {
        contentBrowser {
          ${featureType} {
        ${groupByAttribute} {
        info(filterKey: $filterKey) {
          all
          sequenced
          derived
          primary
          reference
        }
      }
      }
      }
      }
    `,
    variables: {
      filterKey,
    },
    rootField: "contentBrowser",
  };
}

export function getAdminBrowserInfoQueryOptions(
  featureType: string,
  groupByAttribute: string,
  filterKey: string,
): IQueryOptions {
  return {
    query: gql`
      query getAdminBrowserInfo ($filterKey: String) {
        adminBrowser {
          ${featureType} {
        ${groupByAttribute} {
        info(filterKey: $filterKey) {
          all
          sequenced
          derived
          primary
          reference
        }
      }
      }
      }
      }
    `,
    variables: {
      filterKey,
    },
    rootField: "adminBrowser",
  };
}

export function getAdvancedSearchTypesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getAdvancedSearchTypes {
        search {
          advancedSearchConfig {
            ...AdvancedSearchTypes
          }
        }
      }
      ${Fragments.advancedSearchTypes}
    `,
    variables: {},
    rootField: "search.advancedSearchConfig.searchTypes",
  };
}

export function getAdvancedSearchAttributesQueryOptions(entityType: string, subTypeId: number): IQueryOptions {
  return {
    query: gql`
      query getAdvancedSearchAttributes($entityType: String!, $subTypeId: Long) {
        search {
          advancedSearchConfig {
            searchAttributes(entityType: $entityType, subTypeId: $subTypeId) {
              ...AdvancedSearchAttribute
            }
          }
        }
      }
      ${Fragments.advancedSearchAttribute}
    `,
    variables: {
      entityType,
      subTypeId,
    },
    rootField: "search.advancedSearchConfig.searchAttributes",
  };
}

export function getAdvancedSearchPreviewQueryOptions(advancedSearchInput: InputAdvancedSearchInput): IQueryOptions {
  const stringifiedAdvancedSearchInput: string = stringifyLikeJson(advancedSearchInput);

  return {
    query: gql`
      query getAdvancedSearchPreview {
        search {
          advancedSearch(searchParams: ${stringifiedAdvancedSearchInput}) {
            id
            resultSize
            elapsedTime
          }
        }
      }
    `,
    variables: {},
    rootField: "search.advancedSearch",
  };
}

export function getSearchScopeOptions(searchTerm: string, restrictionType: string, limit: number): IQueryOptions {
  return {
    query: gql`
      query searchAdvancedSearchScopes($searchTerm: String, $restrictionType: String, $limit: Int) {
        search {
          searchScopes(searchTerm: $searchTerm, restrictionType: $restrictionType, limit: $limit) {
            id
            restrictionType
            name
            secondaryName
          }
        }
      }
    `,
    variables: {
      searchTerm: searchTerm,
      restrictionType: restrictionType,
      limit: limit,
    },
    rootField: "search.searchScopes",
  };
}

export function getSearchProjectFilesOptions(searchTerms: string[], filterExpression: string): IQueryOptions {
  return {
    query: gql`
      query searchProjectFilesMatching($searchTerms: [String], $extensionRegex: String) {
        search {
          projectFilesSearch(searchTerms: $searchTerms, extensionRegex: $extensionRegex) {
            searchTerm
            projectFiles {
              id
              fileName
              fullPath
            }
          }
        }
      }
    `,
    variables: {
      searchTerms: searchTerms,
      extensionRegex: filterExpression,
    },
    rootField: "search.projectFilesSearch",
  };
}

export function getSetComparison(payload: InputSetComparisonInput): IQueryOptions {
  const viewInputData: string = stringifyLikeJson(payload);
  return {
    query: gql`
      query getComparison {
        setComparison( setComparisonInput: ${viewInputData} ) {
          left
          middle
          right
        }
      }
    `,
    variables: {},
    rootField: "setComparison",
  };
}

export function getFileNameExistsOptions(projectId: number, resourceNames: string[]): IQueryOptions {
  return {
    query: gql`
      query doesFilenameExist($projectId: Long!, $fileNames: [String]) {
        fileNameExists(projectId: $projectId, fileNames: $fileNames) {
          id
          fileName
          deletable
          url
          description
        }
      }
    `,
    variables: {
      projectId: projectId,
      fileNames: resourceNames,
    },
    rootField: "fileNameExists",
  };
}

export function getGenomeViewerFilesByIdOption(fileIds: number[]): IQueryOptions {
  return {
    query: gql`
      query getGenomeViewerFilesById($fileIds: [Long]!) {
        projectFilesById(fileIds: $fileIds) {
          ...GenomeViewerFile
        }
      }
      ${Fragments.genomeViewerFile}
    `,
    variables: {
      fileIds: fileIds,
    },
    rootField: "projectFilesById",
  };
}

export function getOrganismsFromProjectFileByIdOptions(fileIds: number[]): IQueryOptions {
  return {
    query: gql`
      query getOrganismsFromProjectFileByIdOptions($fileIds: [Long]!) {
        projectFilesById(fileIds: $fileIds) {
          id
          organisms {
            id
            name
            species {
              name
            }
          }
        }
      }
    `,
    variables: {
      fileIds: fileIds,
    },
    rootField: "projectFilesById",
  };
}

export function getValidateBamFileAgainstRefGenomesOptions(bamFileId: number, genomeId: number): IQueryOptions {
  return {
    query: gql`
      query validateBamFileAgainstRefGenomes($fileId: Long, $organismId: Long!) {
        genomeViewer {
          validateBamReads(BAMId: $fileId, organismId: $organismId)
        }
      }
    `,
    variables: {
      fileId: bamFileId,
      organismId: genomeId,
    },
    rootField: "genomeViewer.validateBamReads",
  };
}

export function getValidateBedFileAgainstRefGenomesOptions(bedFileId: number, genomeId: number): IQueryOptions {
  return {
    query: gql`
      query validateBedFileAgainstRefGenomes($BEDId: Long, $organismId: Long!) {
        genomeViewer {
          validateBedFeatures(BEDId: $BEDId, organismId: $organismId)
        }
      }
    `,
    variables: {
      BEDId: bedFileId,
      organismId: genomeId,
    },
    rootField: "genomeViewer.validateBedFeatures",
  };
}

export function getValidateSingleGroupAgainstProjectUserGroupOptions(
  projectId: number,
  _type: string,
  tableQuerySpec: InputTableQuerySpec,
  targetType: string,
): IQueryOptions {
  return {
    query: gql`
      query validateSingleGroupAgainstProjectUserGroup(
        $projectId: Long!
        $tableQuerySpec: InputTableQuerySpec!
        $targetType: String!
      ) {
        userGroup {
          validateItems(projectId: $projectId, tableQuerySpec: $tableQuerySpec, targetType: $targetType) {
            totalItemCount
            userGroup
            visibleItemCount
          }
        }
      }
    `,
    variables: {
      projectId: projectId,
      tableQuerySpec: tableQuerySpec,
      targetType: targetType,
    },
    rootField: "userGroup.validateItems",
  };
}

export function getValidateSavedGroupsAgainstProjectUserGroupOptions(
  projectId: number,
  groupIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query validateSavedGroupsAgainstProjectUserGroup($projectId: Long!, $groupIds: [Long]!) {
        userGroup {
          canCopyGroups(groupIds: $groupIds, projectId: $projectId)
        }
      }
    `,
    variables: {
      projectId: projectId,
      groupIds: groupIds,
    },
    rootField: "userGroup.canCopyGroups",
  };
}

export function getValidateSingleViewAgainstProjectUserGroupOptions(params: InputViewInput): IQueryOptions {
  const viewInputData: string = stringifyLikeJson(params);
  return {
    query: gql`
      query validateSingleViewAgainstProjectUserGroup {
        userGroup {
          canSaveView(viewInputData: ${viewInputData})
        }
      }
    `,
    variables: {},
    rootField: "userGroup.canSaveView",
  };
}

export function getValidateSavedViewsAgainstProjectUserGroupOptions(
  projectId: number,
  viewIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query validateViewsAgainstProjectUserGroup($projectId: Long!, $viewIds: [Long]!) {
        userGroup {
          canCopyViews(viewIds: $viewIds, projectId: $projectId)
        }
      }
    `,
    variables: {
      projectId: projectId,
      viewIds: viewIds,
    },
    rootField: "userGroup.canCopyViews",
  };
}

export function getValidateFlagsAgainstProjectUserGroupOptions(projectId: number, flagIds: number[]): IQueryOptions {
  return {
    query: gql`
      query validateFlagsAgainstProjectUserGroup($projectId: Long!, $flagIds: [Long]!) {
        userGroup {
          canCopyFlags(flagIds: $flagIds, projectId: $projectId)
        }
      }
    `,
    variables: {
      projectId: projectId,
      flagIds: flagIds,
    },
    rootField: "userGroup.canCopyFlags",
  };
}

export function getSampleTypeExistsQueryOptions(name: string): IQueryOptions {
  return {
    query: gql`
      query validateSampleTypeExists($name: String!) {
        sample {
          sampleTypeExists(sampleTypeName: $name)
        }
      }
    `,
    variables: {
      name: name,
    },
    rootField: "sample.sampleTypeExists",
  };
}

export function getPlaybookExistsQueryOptions(name: string, suffix: string): IQueryOptions {
  return {
    query: gql`
      query validatePlaybookExists($name: String!, $suffix: String) {
        admin {
          playbookNameExists(playbookName: $name, suffix: $suffix)
        }
      }
    `,
    variables: {
      name: name,
      suffix: suffix,
    },
    rootField: "admin.playbookNameExists",
  };
}

export function getParentTypeExistsQueryOptions(name: string): IQueryOptions {
  return {
    query: gql`
      query validateParentTypeExists($name: String!) {
        sample {
          parentTypeExists(parentTypeName: $name)
        }
      }
    `,
    variables: {
      name: name,
    },
    rootField: "sample.parentTypeExists",
  };
}

export function getIsAcceptableNewOracleAccountNameQueryOptions(username: string): IQueryOptions {
  return {
    query: gql`
      query validateIsAcceptableNewOracleAccountName($username: String!) {
        admin {
          isAcceptableNewOracleAccountName(username: $username)
        }
      }
    `,
    variables: {
      username: username,
    },
    rootField: "admin.isAcceptableNewOracleAccountName",
  };
}

export function getIsParentNameUniqueQueryOptions(name: string, parentId: number): IQueryOptions {
  return {
    query: gql`
      query validateParentNameInput($name: String!, $parentId: Long!) {
        sample {
          validateParentNameInput(name: $name, parentId: $parentId)
        }
      }
    `,
    variables: {
      name: name,
      parentId: parentId,
    },
    rootField: "sample.validateParentNameInput",
  };
}

// TODo extract sampleType.parentTypeSets.parentType.parents and fetch it only when necessary (recyclabel parent)

// for sample based playbook
export function getSampleTypeWizardQueryOptions(
  sampleTypeId: number,
  excludeAnalyticalAttributes = false,
): IQueryOptions {
  return {
    query: gql`
      query getSampleType($sampleTypeId: Long!, $excludeAnalytical: Boolean!) {
        sample {
          sampleType(sampleTypeId: $sampleTypeId) {
            id
            name
            parentTypeSets {
              isPrimary
              isRecyclable
              parentType {
                id
                name
                parents {
                  id
                  name
                }
                parentAttributes {
                  representedColumn
                  ...SampleAttributeWithDynamicTerms
                  tableColumn {
                    ...TableColumn
                  }
                }
              }
            }
            statusSet {
              statuses {
                id
                name
              }
            }
            sampleAttributes(excludeAnalytical: $excludeAnalytical) {
              ...SampleAttributeWithDynamicTerms
              tableColumn {
                ...TableColumn
              }
            }
          }
        }
      }
      ${Fragments.sampleAttributeWithDynamicTerms}
      ${Fragments.tableColumn}
    `,
    variables: { sampleTypeId: sampleTypeId, excludeAnalytical: excludeAnalyticalAttributes },
    rootField: "sample.sampleType",
  };
}

export function getSampleEditingColumnQueryOptions(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getSampleAttributes($tableQuerySpec: InputTableQuerySpec!) {
        sample {
          editSamplesGrid(tableQuerySpec: $tableQuerySpec) {
            sampleAttributes {
              ...SampleAttributeWithDynamicTerms
              sampleTypes {
                id
                name
              }
            }
          }
        }
      }
      ${Fragments.sampleAttributeWithDynamicTerms}
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "sample.editSamplesGrid",
  };
}

export function getSampleEditingDataQueryOptions(
  tableQuerySpec: InputTableQuerySpec,
  attributeIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query getSamples($tableQuerySpec: InputTableQuerySpec!, $attributeIds: [Long]) {
        sample {
          editSamplesGrid(tableQuerySpec: $tableQuerySpec) {
            samples {
              id
              type {
                id
                name
                allowManualStatusChange
                statusSet {
                  id
                }
              }
              name
              description
              organism {
                id
                name
                species {
                  id
                  name
                }
              }
              locked
              valuesByAttributeId(attributeIds: $attributeIds) {
                valueList {
                  ...EditingAttributeValue
                }
                attribute {
                  id
                }
              }
            }
          }
        }
      }
      ${Fragments.editingAttributeValue}
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
      attributeIds: attributeIds,
    },
    rootField: "sample.editSamplesGrid",
  };
}

export function getSampleTypeForPlaybooksButton(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getSampleTypeForPlaybooksButton($tableQuerySpec: InputTableQuerySpec!) {
        sample {
          editSamplesGrid(tableQuerySpec: $tableQuerySpec) {
            samples {
              id
              type {
                id
              }
            }
          }
        }
      }
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "sample.editSamplesGrid",
  };
}

export function getCheckAttributeUniquenessQueryOptions(
  samplesValidationInputData: InputSamplesValidationInputData,
): IQueryOptions {
  return {
    query: gql`
      query checkAttributeUniqueness($samplesValidationInput: InputSamplesValidationInputData!) {
        sample {
          validateSamplesInput(samplesValidationInput: $samplesValidationInput)
        }
      }
    `,
    variables: {
      samplesValidationInput: samplesValidationInputData,
    },
    rootField: "sample.validateSamplesInput",
  };
}

export function getLoadColumnTemplateQueryOptions(columnTemplate, tableSpec): IQueryOptions {
  const { context, ...cleanedUpTableQuerySpec } = tableSpec; // remove the context if any
  return {
    query: gql`
      query loadColumnTemplate($templateId: Long!, $tableSpec: InputTableQuerySpec!) {
        template {
          loadTemplate(templateId: $templateId, tableSpec: $tableSpec) {
            ...Template
          }
        }
      }
      ${Fragments.columnTemplate}
    `,
    variables: {
      templateId: columnTemplate.id,
      tableSpec: cleanedUpTableQuerySpec,
    },
    rootField: "template.loadTemplate",
  };
}

// name, description, categories, types, disabled, roles
export function getLoadAllCapabilitiesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query loadAllCapabilities {
        admin {
          capabilities {
            ...Capability
            playbook {
              id
              name
              nameSuffix
              enabled
              type {
                name
              }
              description
              roles {
                id
                name
              }
              categories {
                id
                name
              }
            }
          }
        }
      }
      ${Fragments.capability}
    `,
    variables: {},
    rootField: "admin.capabilities",
  };
}

export function getLoadAllRolesQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query loadAllRoles {
        admin {
          roles {
            ...RoleAdmin
          }
        }
      }
      ${Fragments.roleAdmin}
    `,
    variables: {},
    rootField: "admin.roles",
  };
}

export function getTranslateAnalyzerIdsOptions(data: InputAnalyzerIdMappingInput): IQueryOptions {
  return {
    query: gql`
      query analyzerIdMapping($analyzerIdMappingInput: InputAnalyzerIdMappingInput!) {
        analyzerIdMapping(analyzerIdMappingInput: $analyzerIdMappingInput) {
          entityType
          idRanges {
            id
            range {
              start
              stop
            }
          }
        }
      }
    `,
    variables: {
      analyzerIdMappingInput: data,
    },
    rootField: "analyzerIdMapping",
  };
}

export function getGenomeAssemblies(ids: number[]): IQueryOptions {
  return {
    query: gql`
      query genomeAssemblies($ids: [Long]) {
        admin {
          tools {
            genomeAssemblies(ids: $ids) {
              id
              assemblyAccession
              taxid
              organismName
              intraspecificName
              isolate
              releaseDate
              assemblyName
              status
              submitter
              url
              moleculeCount
              scaffoldCount
              contigCount
              totalLength
            }
          }
        }
      }
    `,
    variables: {
      ids: ids,
    },
    rootField: "admin.tools.genomeAssemblies",
  };
}

export function getAllAdminTools(): IQueryOptions {
  return {
    query: gql`
      query allAdminTools {
        admin {
          tools {
            allAdminTools {
              id
              name
              description
              url
              requireInternetConnection
            }
          }
        }
      }
    `,
    variables: {},
    rootField: "admin.tools.allAdminTools",
  };
}

export function getAllTaxonomies(): IQueryOptions {
  return {
    query: gql`
      query allTaxonomies {
        admin {
          tools {
            allTaxonomies {
              id
              name
            }
          }
        }
      }
    `,
    variables: {},
    rootField: "admin.tools.allTaxonomies",
  };
}

export function hasInternetConnectionOptions(url: string | null): IQueryOptions {
  return {
    query: gql`
      query hasInternetConnection($url: String) {
        admin {
          tools {
            hasInternetConnection(url: $url)
          }
        }
      }
    `,
    variables: { url },
    rootField: "admin.tools.hasInternetConnection",
  };
}

export function isValidGenomeName(name: string): IQueryOptions {
  return {
    query: gql`
      query isValidGenomeName($name: String!) {
        isValidGenomeName(name: $name)
      }
    `,
    variables: { name },
    rootField: "isValidGenomeName",
  };
}
export function isValidGenomeIdentifier(identifier: string): IQueryOptions {
  return {
    query: gql`
      query isValidGenomeIdentifier($identifier: String!) {
        isValidGenomeIdentifier(identifier: $identifier)
      }
    `,
    variables: { identifier },
    rootField: "isValidGenomeIdentifier",
  };
}

export function resolveTemplateNextElement(
  sampleTypeId: number,
  values: InputAttributeValueInput[],
  sequence: string[],
  loopOverSequence: boolean,
): IQueryOptions {
  return {
    query: gql`
      query translateTemplatesNextElement(
        $sampleTypeId: Long!
        $values: [InputAttributeValueInput!]!
        $sequence: [String!]!
        $loopOverSequence: Boolean!
      ) {
        sample {
          templateNextElement(
            sampleTypeId: $sampleTypeId
            values: $values
            sequence: $sequence
            loopOverSequence: $loopOverSequence
          )
        }
      }
    `,
    variables: {
      sampleTypeId: sampleTypeId,
      values: values,
      sequence: sequence,
      loopOverSequence: loopOverSequence,
    },
    rootField: "sample.templateNextElement",
  };
}

export function resolveTemplateNext(sampleTypeId: number, values: InputAttributeValueInput[]): IQueryOptions {
  return {
    query: gql`
      query translateTemplateNext($sampleTypeId: Long!, $values: [InputAttributeValueInput!]!) {
        sample {
          templateNext(sampleTypeId: $sampleTypeId, values: $values)
        }
      }
    `,
    variables: {
      sampleTypeId: sampleTypeId,
      values: values,
    },
    rootField: "sample.templateNext",
  };
}

export function resolveTemplateCount(sampleTypeId: number, values: InputAttributeValueInput[]): IQueryOptions {
  return {
    query: gql`
      query translateTemplateCount($sampleTypeId: Long!, $values: [InputAttributeValueInput!]!) {
        sample {
          templateCount(sampleTypeId: $sampleTypeId, values: $values)
        }
      }
    `,
    variables: {
      sampleTypeId: sampleTypeId,
      values: values,
    },
    rootField: "sample.templateCount",
  };
}

export function getNumberOfFreeSeatsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getNumberOfFreeSeats {
        configuration {
          license {
            numberOfFreeSeats
          }
        }
      }
    `,
    variables: {},
    rootField: "configuration.license.numberOfFreeSeats",
  };
}

export function getConfigurationQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getConfiguration {
        configuration {
          authenticationMode
          canCreateOracleUser
          auditLogEnabled
          systemParameters {
            projectLabel
          }
        }
      }
    `,
    variables: {},
    rootField: "configuration",
  };
}

export function getFetchTestGenomicGeneOptions(tableSpec: InputTableQuerySpec) {
  return {
    query: gql`
      query getGeneDataRows($tableQuerySpec: InputTableQuerySpec) {
        table(tableQuerySpec: $tableQuerySpec) {
          pageRows {
            id
            ... on Gene {
              genome {
                id
              }
            }
          }
        }
      }
    `,
    variables: {
      tableQuerySpec: tableSpec,
    },
    rootField: "table",
  };
}

export function getFetchSystemDetailsOptions(systemId: string) {
  return {
    query: gql`
      query fetchSystemDetails($module: String) {
        admin {
          server {
            status(module: $module) {
              build
              jre
              os
              remote
              runtime
              version
              memTotal
              memUsed
            }
          }
        }
      }
    `,
    variables: {
      module: systemId,
    },
    rootField: "admin.server.status",
  };
}

export function getFetchSampleTypeStatusOptions(config: ISampleStatusesCardConfig) {
  return {
    query: gql`
      query fetchSampleTypeStatuses($sampleType: Long!, $statuses: [Long!]!) {
        sample {
          sampleCounts(sampleType: $sampleType, statuses: $statuses) {
            sampleType {
              id
              name
              description
            }
            parentType {
              id
              name
              description
            }
            sampleCounts {
              sampleStatus {
                id
                name
                description
                statusClass {
                  name
                }
              }
              sampleCount
              parentCount
              modified
            }
          }
        }
      }
    `,
    variables: {
      sampleType: config.sampleTypeId,
      statuses: config.statusIds,
    },
    context: {
      skipErrorLink: true,
    },
    rootField: "sample.sampleCounts",
  };
}

export function getCheckSequencedOrganismsOptions(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getCheckSequenceOrganisms($tableQuerySpec: InputTableQuerySpec) {
        checkSequencedOrganisms(tableQuerySpec: $tableQuerySpec)
      }
    `,
    variables: {
      tableQuerySpec: tableQuerySpec,
    },
    rootField: "checkSequencedOrganisms",
  };
}

export function getFetchPlaybooksOptions(): IQueryOptions {
  return {
    query: gql`
      query getPlaybooks {
        playbook {
          playbooks {
            ...PlaybookBasics
          }
        }
      }
      ${Fragments.playbookBasics}
    `,
    variables: {},
    rootField: "playbook.playbooks",
  };
}

export function getFetchPlaybookCategories(): IQueryOptions {
  return {
    query: gql`
      query getPlaybookCategories {
        admin {
          playbookCategories {
            ...PlaybookCategoryBasics
          }
        }
      }
      ${Fragments.playbookCategoryBasics}
    `,
    variables: {},
    rootField: "admin.playbookCategories",
  };
}

export function getFetchPlaybookCategoryById(categoryId: number): IQueryOptions {
  return {
    query: gql`
      query playbookCategoryById($categoryId: Long!) {
        playbook {
          getCategoryById(categoryId: $categoryId) {
            id
            name
          }
        }
      }
    `,
    variables: { categoryId: categoryId },
    rootField: "playbook.getCategoryById",
  };
}

export function getFetchAdminPlaybooksOptions(): IQueryOptions {
  return {
    query: gql`
      query getAdminPlaybooks {
        admin {
          playbooks {
            ...PlaybookBasics
            roles {
              name
            }
          }
        }
      }
      ${Fragments.playbookBasics}
    `,
    variables: {},
    rootField: "admin.playbooks",
  };
}

export function getFetchSampleIdsForParentIdOptions(parentIds: number[]): IQueryOptions {
  return {
    query: gql`
      query getSampleIdsForParentIds($parentIds: [Long]!) {
        sample {
          samplesForParents(parentIds: $parentIds)
        }
      }
    `,
    variables: { parentIds: parentIds },
    rootField: "sample.samplesForParents",
  };
}

export function getFetchPlaybookOptions(playbookId: number): IQueryOptions {
  return {
    query: gql`
      query getPlaybook($playbookId: Long!) {
        playbook {
          playbook(playbookId: $playbookId) {
            ...PlaybookBasics
            templateEvent {
              primaryParent {
                attributeValues {
                  ...EditingAttributeValue
                }
              }
              relatedSamples {
                valuesByAttributeId(columnSetIds: [1]) {
                  valueList {
                    ...EditingAttributeValue
                  }
                  attribute {
                    id
                  }
                }
              }
            }
          }
        }
      }
      ${Fragments.playbookBasics}
      ${Fragments.editingAttributeValue}
    `,
    variables: {
      playbookId: playbookId,
    },
    rootField: "playbook.playbook",
  };
}

export function getFetchNfPlaybookOptions(playbookId: number): IQueryOptions {
  return {
    query: gql`
      query getNfPlaybook($playbookId: Long!) {
        playbook {
          playbook(playbookId: $playbookId) {
            ...PlaybookBasics
            nfConfig {
              ...NFPlaybookConfig
            }
            p2Config {
              workflow {
                id
                name
              }
              ...ProcessorExtendedPlaybookConfig
            }
          }
        }
      }
      ${Fragments.playbookBasics}
      ${Fragments.nfConfigFragment}
      ${Fragments.processorExtendedPlaybookConfig}
    `,
    variables: {
      playbookId: playbookId,
    },
    rootField: "playbook.playbook",
  };
}

export function getFetchAdminPlaybookOptions(playbookId: number): IQueryOptions {
  return {
    query: gql`
      query getAdminPlaybook($playbookId: Long!) {
        admin {
          playbook(playbookId: $playbookId) {
            ...PlaybookBasics
            reportTemplateResources {
              name
              size
              type
            }
            nfConfig {
              ...NFPlaybookConfig
            }
            p2Config {
              ...P2PlaybookConfig
            }
          }
        }
      }
      ${Fragments.playbookBasics}
      ${Fragments.nfConfigFragment}
      ${Fragments.p2PlaybookConfig}
    `,
    variables: {
      playbookId: playbookId,
    },
    rootField: "admin.playbook",
  };
}

export function getFetchNfWorkflowsOptions(): IQueryOptions {
  return {
    query: gql`
      query getNfWorkflows {
        playbook {
          nfWorkflows {
            name
          }
        }
      }
    `,
    variables: {},
    rootField: "playbook.nfWorkflows",
  };
}

export function getFetchPublishedWorkflowsOptions(): IQueryOptions {
  return {
    query: gql`
      query getPublishedWorkflows {
        admin {
          publishedWorkflows {
            id
            name
          }
        }
      }
    `,
    variables: {},
    rootField: "admin.publishedWorkflows",
  };
}

export function getFetchNfWorkflowOptions(workflowName: string): IQueryOptions {
  return {
    query: gql`
      query getNfWorkflow($workflowId: String) {
        playbook {
          nfWorkflow(workflowId: $workflowId) {
            name
            activities {
              name
              hpcEnabled
            }
            profiles
            parameters {
              identifier
              type {
                type
                subType
              }
              defaultValue
            }
          }
        }
      }
    `,
    variables: { workflowId: workflowName },
    rootField: "playbook.nfWorkflow",
  };
}

export function getFetchP2WorkflowOptions(workflowId: number): IQueryOptions {
  return {
    query: gql`
      query getP2Workflow($workflowId: Long) {
        workflow {
          findWorkflowById(workflowId: $workflowId) {
            id
            name
            extendedConfig {
              ...WorkflowExtendedConfig
            }
          }
        }
      }
      ${Fragments.workflowExtendedConfig}
    `,
    variables: { workflowId: workflowId },
    rootField: "workflow.findWorkflowById",
  };
}

export function getLoadP2WorkflowForParamsValidationOptions(workflowId: number): IQueryOptions {
  return {
    query: gql`
      query loadP2Workflow($workflowId: Long) {
        workflow {
          findWorkflowById(workflowId: $workflowId) {
            id
            extendedConfig {
              activities {
                disabled
                parameters {
                  __typename
                  id
                  staged
                  disabled
                  parameterDefinition {
                    __typename
                    type
                    disabled
                    ... on TableParam {
                      tableType
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: { workflowId: workflowId },
    rootField: "workflow.findWorkflowById",
  };
}

export function getFetchApprovedWorkflowsOptions(
  workflowID: string /* should be null if all wf are to be found */,
  playbookTypeId: number,
): IQueryOptions {
  return {
    query: gql`
      query getApprovedWorkflows($workflowID: String, $playbookTypeId: Long) {
        playbook {
          approvedWorkflows(workflowID: $workflowID, playbookTypeId: $playbookTypeId) {
            name
            sourceId
            exportedFields {
              name
              typeId
              description
              isMultiValues
            }
            workflowParams {
              id
              activityId
              activityName
              activityIndex
              label
              name
              required
              description
              valueTypeId
              usage
              optionKeys
              optionLabels
              values
              multiSelect
              defaultParams {
                values
                files {
                  ...ProjectFileTile
                }
                organism {
                  ...OrganismTile
                }
                selectedIndex
              }
            }
            approvalDate
            isDemo
            isValid
          }
        }
      }
      ${Fragments.organismTile}
      ${Fragments.projectFileTile}
    `,
    variables: { workflowID: workflowID, playbookTypeId: playbookTypeId },
    rootField: "playbook.approvedWorkflows",
  };
}

// --- Files ---
export function fetchProjectFilesForAttachmentsQueryOptions(projectId: number) {
  return {
    query: gql`
      query fetchAllFilesInProjectForAttachments($projectId: Long!) {
        projectFiles(projectId: $projectId) {
          id
          created
          creator {
            ...UserBasics
          }
          organisms {
            id
            name
            species {
              name
            }
          }
          parents {
            id
            name
            parentType {
              name
            }
          }
          description
          fileName
          fileType
          lastModified
          typeDisplayName
          contentType {
            name
            description
            id
          }
        }
      }
      ${Fragments.userBasics}
    `,
    variables: { projectId },
    rootField: "projectFiles",
  };
}

export function getFileNameExistsInCollector(folderId: number, fileNames: string[]) {
  return {
    query: gql`
      query getFileNameExistsInCollector($folderId: Long, $fileNames: [String]) {
        fileNameExistsInCollector(folderId: $folderId, fileNames: $fileNames)
      }
    `,
    variables: { fileNames, folderId },
    rootField: "fileNameExistsInCollector",
  };
}

export function getfetchAllOrgPropertyTypesQueryOptions() {
  return {
    query: gql`
      query getOrgPropertyTypes {
        organismPropertyTypes {
          ...OrganismPropertyTypeBasics
        }
      }
      ${Fragments.organismPropertyTypeBasics}
    `,
    variables: {},
    rootField: "organismPropertyTypes",
  };
}

export function getfetchOrgPropertyTypeQueryOptions(orgPropertyTypeId: number) {
  return {
    query: gql`
      query getOrgPropertyType($orgPropertyTypeId: Long) {
        organismPropertyType(orgPropertyTypeId: $orgPropertyTypeId) {
          ...OrganismPropertyTypeBasics
          description
          valueType
          properties {
            id
            color
            description
            value
          }
        }
      }
      ${Fragments.organismPropertyTypeBasics}
    `,
    variables: { orgPropertyTypeId: orgPropertyTypeId },
    rootField: "organismPropertyType",
  };
}

export function getfetchApplicableOrgPropertyTypesQueryOptions(orgIds: number[]) {
  return {
    query: gql`
      query getApplicableOrgPropertyTypes($organismIds: [Long]) {
        genomeViewer {
          phenotypeTypesByOrganism(organismIds: $organismIds) {
            id
            idType
            name
            groupName
            description
            groupDescription
            isDiscrete
            valueType
            discreteValues {
              color
              description
              value
              numValue
            }
          }
        }
      }
    `,
    variables: { organismIds: orgIds },
    rootField: "genomeViewer.phenotypeTypesByOrganism",
  };
}

export function getFetchPhenotypeValuesQueryOptions(orgIds: number[], phenotypeIds: number[], attributeIds: number[]) {
  return {
    query: gql`
      query getValuesForOrganismPropertyTypesAndOrganisms(
        $organismIds: [Long]
        $organismPropertyTypeIds: [Long]
        $attributeIds: [Long]
      ) {
        genomeViewer {
          valuesForOrganismPropertyTypesAndOrganisms(
            organismIds: $organismIds
            organismPropertyTypeIds: $organismPropertyTypeIds
            attributeIds: $attributeIds
          ) {
            organism {
              id
            }
            phenotypeValues {
              color
              value
              numValue
              phenotypeTypeData {
                id
                idType
                name
              }
            }
          }
        }
      }
    `,
    variables: { organismIds: orgIds, organismPropertyTypeIds: phenotypeIds, attributeIds: attributeIds },
    rootField: "genomeViewer.valuesForOrganismPropertyTypesAndOrganisms",
  };
}

export function getFetchSequenceFeatureTypesQueryOptions() {
  return {
    query: gql`
      query getSequenceFeatureTypes {
        sequenceFeatureTypes {
          markerTypes {
            ...MarkerType
          }
          nuFeatureTypes {
            ...NuFeatureType
          }
          aaFeatureTypes {
            ...AaFeatureType
          }
        }
      }
      ${Fragments.markerType}
      ${Fragments.aaFeatureType}
      ${Fragments.nuFeatureType}
    `,
    variables: {},
    rootField: "sequenceFeatureTypes",
  };
}

export function getFetchSequenceTypesQueryOptions() {
  return {
    query: gql`
      query getSequenceTypes {
        sequenceTypes {
          id
          name
          label
          groupType
        }
      }
    `,
    rootField: "sequenceTypes",
  };
}

export function getFetchValuesForOrganismPropertyTypeQueryOptions(orgPropertyTypeId: number, searchTerm: string) {
  return {
    query: gql`
      query getValuesForOrganismPropertyType($orgPropertyTypeId: Long, $searchTerm: String) {
        valuesForOrganismPropertyType(orgPropertyTypeId: $orgPropertyTypeId, searchTerm: $searchTerm) {
          id
          value
          color
        }
      }
    `,
    variables: {
      orgPropertyTypeId: orgPropertyTypeId,
      searchTerm: searchTerm,
    },
    rootField: "valuesForOrganismPropertyType",
  };
}

export function getMarkerQueryOptions(markerId: number): IQueryOptions {
  return {
    query: gql`
      query getMarker($markerId: Long!) {
        marker(markerId: $markerId) {
          ...MarkerSummary
        }
      }
      ${Fragments.markerSummary}
    `,
    variables: {
      markerId: markerId,
    },
    rootField: "marker",
  };
}

export function getNuFeatureQueryOptions(nuFeatureId: number): IQueryOptions {
  return {
    query: gql`
      query getNuFeature($nuFeatureId: Long!) {
        nuFeature(nuFeatureId: $nuFeatureId) {
          name
          id
          description
          pValue
          score
          groupType
          nuFeatureOrganismProperties {
            organismProperty {
              id
              color
              description
              numValue
              value
              organismPropertyType {
                id
                name
                description
                sortOrder
                isDiscrete
                valueType
                group {
                  opgId
                  name
                  sortOrder
                  description
                }
              }
            }
            source {
              id
              owner
              sourceType {
                id
                name
              }
              organisation {
                id
                name
                description
                url
                note
              }
            }
          }
          externalReferences {
            ...ExternalReference
          }
          region {
            ...RegionBasics
          }
          gene {
            ...GeneBasics
          }
          relatedProjects {
            ...RelatedProject
          }
          genome {
            ...GenomeInfoCard
            components {
              ...OrganismTile
            }

            virtualGenomes {
              ...OrganismTile
              sequencingType
            }
          }
          nuFeatureType {
            id
            name
            color
          }
        }
      }
      ${Fragments.geneBasics}
      ${Fragments.regionBasics}
      ${Fragments.externalReference}
      ${Fragments.genomeInfoCard}
      ${Fragments.organismTile}
      ${Fragments.relatedProject}
    `,
    variables: {
      nuFeatureId: nuFeatureId,
    },
    rootField: "nuFeature",
  };
}

export function getAaFeatureQueryOptions(aaFeatureId: number): IQueryOptions {
  return {
    query: gql`
      query getAaFeature($aaFeatureId: Long!) {
        aaFeature(aaFeatureId: $aaFeatureId) {
          name
          id
          description
          pValue
          score
          groupType
          region {
            ...RegionBasics
          }
          protein {
            ...ProteinBasics
          }
          genome {
            ...GenomeInfoCard
            components {
              ...OrganismTile
            }

            virtualGenomes {
              ...OrganismTile
              sequencingType
            }
          }
          aaFeatureType {
            id
            name
            color
          }
        }
      }
      ${Fragments.proteinBasics}
      ${Fragments.regionBasics}
      ${Fragments.genomeInfoCard}
      ${Fragments.organismTile}
    `,
    variables: {
      aaFeatureId: aaFeatureId,
    },
    rootField: "aaFeature",
  };
}

export function getEventQueryOptions(eventId: number): IQueryOptions {
  return {
    query: gql`
      query getEvent($eventId: Long!) {
        playbook {
          event(eventId: $eventId) {
            ...EventSummary
            workflowP2ExtendedConfig {
              sampleType {
                id
                name
              }
              connectors {
                ...WorkflowConnector
              }
              activities {
                identifier
                name
                label
                status
                version
                disabled
                parameters {
                  ...WorkflowExtendedParameter
                }
                metadata {
                  width
                  position {
                    x
                    y
                  }
                }
                executor
              }
            }
          }
        }
      }
      ${Fragments.eventSummary}
      ${WorkflowConnectorFragmentDoc}
      ${WorkflowExtendedParameterFragmentDoc}
    `,
    variables: {
      eventId: eventId,
    },
    rootField: "playbook.event",
  };
}

export function getEventForRerunQueryOptions(eventId: number): IQueryOptions {
  return {
    query: gql`
      query getEventForRerun($eventId: Long!) {
        playbook {
          event(eventId: $eventId) {
            inputs {
              id
              label
              workflowField
              inputValues
              resources {
                ...EventResource
              }
            }
            project {
              id
              name
              description
              color
            }
            workflowP2ExtendedConfig {
              ...ProcessorExtendedWorkflowConfig
            }
          }
        }
      }
      ${Fragments.eventResource}
      ${Fragments.processorExtendedWorkflowConfig}
    `,
    variables: {
      eventId: eventId,
    },
    rootField: "playbook.event",
  };
}

export function getRecentEventsQueryOptions(
  eventsLimit: number,
  categoryId?: number,
  offset = 0,
  currentUserOnly = true,
): IQueryOptions {
  return {
    query: gql`
      query getRecentEvents($eventsLimit: Long!, $categoryId: Long, $offset: Long!, $currentUserOnly: Boolean!) {
        playbook {
          recentEvents(
            eventsLimit: $eventsLimit
            categoryId: $categoryId
            offset: $offset
            currentUserOnly: $currentUserOnly
          ) {
            ...PlaybookEventTile
          }
        }
      }
      ${Fragments.playbookEventTile}
    `,
    variables: { eventsLimit, categoryId, offset, currentUserOnly },
    rootField: "playbook.recentEvents",
  };
}

export function getSequenceGroupTypesQueryOptions(tableQuerySpec: InputTableQuerySpec): IQueryOptions {
  return {
    query: gql`
      query getSequenceGroupTypes($tableQuerySpec: InputTableQuerySpec!) {
        sequenceGroupTypes(tableQuerySpec: $tableQuerySpec) {
          genericSeqIds
          sequenceGroup {
            name
          }
        }
      }
    `,
    variables: { tableQuerySpec },
    rootField: "sequenceGroupTypes",
  };
}

export function getMutantGenomesByAnalysisGroupQueryOptions(
  analysisGroup: VariantAnalysisGroup,
  mutantGenomesIds: number[],
): IQueryOptions {
  return {
    query: gql`
      query getMutantGenomesByAnalysisGroup($analysisGroupId: Long!, $mutantGenomesIds: [Long]) {
        genomeViewer {
          analysisGroup(analysisGroupId: $analysisGroupId) {
            genomes(genomeIds: $mutantGenomesIds) {
              id
              name
              isReference
              species {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: { analysisGroupId: analysisGroup.id, mutantGenomesIds: mutantGenomesIds },
    rootField: "genomeViewer.analysisGroup.genomes",
  };
}

export function getRelatedAnalysisGroupsQueryOptions(organismId: number): IQueryOptions {
  return {
    query: gql`
      query getRelatedAnalysisGroups($genomeId: Long!) {
        organism(organismId: $genomeId) {
          id
          variantAnalysisGroups {
            id
            name
          }
        }
      }
    `,
    variables: { genomeId: organismId },
    rootField: "organism.variantAnalysisGroups",
  };
}

export function getCheckVirtualGenomeExistsOptions(genomeIds: number[]): IQueryOptions {
  return {
    query: gql`
      query checkVirtualGenomeExists($genomeIds: [Long]!) {
        getVirtualGenomesForComponents(genomeIds: $genomeIds) {
          id
          name
          species {
            id
            name
          }
        }
      }
    `,
    variables: { genomeIds: genomeIds },
    rootField: "getVirtualGenomesForComponents",
  };
}

export function getFetchAuditLogExportFilterOptionsQueryOptions(): IQueryOptions {
  return {
    query: gql`
      query getAuditLogExportFilterOptions {
        auditFilterOptions {
          eventTypes {
            id
            name
          }
          playbooks {
            id
            name
            nameSuffix
          }
          roles {
            ...RoleBasics
          }
          users {
            ...UserBasics
          }
        }
      }
      ${Fragments.userBasics}
      ${Fragments.roleBasics}
    `,
    rootField: "auditFilterOptions",
  };
}
